<template>
  <div class="live-transcript-overlay" v-show="isVisible">
    <div
      class="live-transcript-container ultra-modern"
      :style="{
        background: backgroundColorLocal,
        width: isMobile ? 'auto' : containerWidth,
        height: containerHeight
      }"
      v-if="displayText"
    >
      <!-- Background overlay with gradient -->
      <div class="background-overlay"></div>

      <!-- Animated background elements -->
      <div class="animated-bg-elements">
        <div v-for="n in 3" :key="`bg-element-${n}`" class="bg-element"></div>
      </div>

      <!-- Speaker section with enhanced visuals -->
      <div class="speaker-section">
        <div class="speaker-name-container" v-if="showSpeaker">
          <div class="speaker-badge">
            <div class="speaker-status" :class="{ 'active': !isPaused }"></div>
            <span>{{ currentSpeaker }}</span>
          </div>
        </div>

        <div class="transcript-speaker-avatar" :class="{ 'speaking': !isPaused }">
          <div class="avatar-glow" :class="{ 'active': !isPaused }"></div>
          <img
            v-if="speakerImage"
            :src="speakerImage"
            :alt="currentSpeaker"
            class="avatar-image"
            @error="handleImageError"
          />
          <div v-else class="avatar-fallback">
            {{ getSpeakerInitials() }}
          </div>

          <!-- Enhanced audio wave visualization -->
          <div class="avatar-wave-indicator" :class="{ 'active': !isPaused }">
            <div v-for="n in 5" :key="`wave-${n}`" class="wave-bar"></div>
          </div>
        </div>
      </div>

      <!-- Content section with modern text display -->
      <div class="transcript-content">
        <div class="transcript-text-container">
          <div class="transcript-text">
            <span v-html="typewriterText"></span>
            <span class="cursor" :class="{ 'blink': cursorBlinking }">|</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LiveTranscript',
  props: {
    // Current segment text being spoken
    text: {
      type: String,
      default: ''
    },
    // Speaker name
    speaker: {
      type: String,
      default: ''
    },
    // Speaker image URL
    speakerImage: {
      type: String,
      default: ''
    },
    // Whether to show the transcript
    visible: {
      type: Boolean,
      default: true
    },
    // Whether to show the speaker name
    showSpeaker: {
      type: Boolean,
      default: true
    },
    // Maximum length before truncating text
    maxLength: {
      type: Number,
      default: 400
    },
    messageId: {
      required: true,
    },
    // Base typing speed in milliseconds per character
    typingSpeed: {
      type: Number,
      default: 48
    },
    // Whether to restart typing animation when text changes
    restartOnTextChange: {
      type: Boolean,
      default: true
    },
    // Voice playback speed (1.0 = normal)
    voiceSpeed: {
      type: Number,
      default: 1.0
    },
    // Background color for the transcript container
    backgroundColor: {
      type: String,
      default: 'rgba(51, 33, 186, 0.6)' // Restore original default
    }
  },
  data() {
    return {
      backgroundColorLocal: localStorage.getItem(`transcript_background_${this.messageId}`) || this.backgroundColor,
      isVisible: this.visible,
      currentSpeaker: this.speaker,
      fullText: this.text,
      displayText: '',
      typewriterText: '',
      typingIndex: 0,
      typingTimer: null,
      cursorBlinking: false,
      cursorTimer: null,
      isPaused: false,
      pauseTimer: null,
      // Container dimensions - calculated before animation starts
      containerWidth: 'auto',
      containerHeight: 'auto',
      // Text dimensions
      textWidth: 0,
      textHeight: 0,
      // Base container sizes
      minContainerWidth: 300,
      maxContainerWidthDesktop: '70%', // Desktop max width as percentage
      maxContainerWidthMobile: '90%',  // Mobile max width as percentage
      maxWidthInPixels: 800,           // Absolute maximum width in pixels
      minContainerHeight: 120,         // Increased min height to prevent cutoff
      // Character width estimation for pre-sizing (pixels per character)
      charWidthEstimate: 12,
      // Line height estimation (pixels)
      lineHeightEstimate: 25,
      // Padding and other space considerations
      horizontalPadding: 36, // 18px on each side
      verticalPadding: 24, // 12px on each side
      avatarWidth: 100,
      avatarSpacing: 16,
      // Flag to track if we're on mobile
      isMobile: false,
      // Punctuation that indicates pauses with relative pause length multipliers
      pausePunctuation: {
        '.': 10,  // End of sentence - longest pause
        '!': 10,  // Exclamation
        '?': 10,  // Question
        ',': 5,   // Comma - medium pause
        ';': 5,   // Semicolon
        ':': 5,   // Colon
        '-': 5,   // Dash - short pause
        '—': 5,   // Em dash
        '(': 2,   // Opening parenthesis
        ')': 2,   // Closing parenthesis
        '\n': 5,  // Line break - long pause
        '\r': 5   // Carriage return
      },
      // Regular expressions for special text patterns
      regexPatterns: {
        // Match citation patterns like **c3**, **c4**
        citation: /\*\*c\d+\*\*/g,
        // Match common abbreviations with periods
        abbreviation: /(?:\b[A-Z]\.)+(?:[A-Z]\b|[A-Z]\.)/g
      }
    }
  },
  watch: {
    text: {
      immediate: true,
      handler(newText) {
        this.fullText = newText;
        this.processText();

        // Calculate container size immediately based on text length
        this.calculateContainerSize();

        // Restart typewriter if text changes and restartOnTextChange is true
        if (this.restartOnTextChange && this.visible) {
          this.resetTypewriter();
        }
      }
    },
    speaker(newSpeaker) {
      this.currentSpeaker = newSpeaker;
    },
    visible(newVisible) {
      const oldVisible = this.isVisible;
      this.isVisible = newVisible;

      // Stop typewriter when hidden
      if (!newVisible) {
        this.stopTypewriter();
        this.clearPauseTimer();
        this.cursorBlinking = false;
        this.isPaused = true;
      } else if (this.displayText) {
        // When becoming visible again
        // Load the background color from local storage
        this.backgroundColorLocal = localStorage.getItem(`transcript_background_${this.messageId}`) || this.backgroundColor;

        // Calculate container size before resuming
        this.calculateContainerSize();

        // If we're going from hidden to visible, reset the typewriter
        if (!oldVisible && newVisible) {
          // Reset typewriter completely when becoming visible again
          this.resetTypewriter();
        } else {
          // Otherwise, just start from where it left off
          setTimeout(() => {
            this.startTypewriter();
          }, 200);
        }
      }
    },
    voiceSpeed() {
      // Reset typewriter when speed changes to adjust typing speed
      if (this.typingIndex < this.displayText.length) {
        this.resetTypewriter();
      }
    },
    backgroundColor(newColor) {
      // Update local background color when prop changes
      this.backgroundColorLocal = newColor;
      localStorage.setItem(`transcript_background_${this.messageId}`, newColor);
    }
  },
  mounted() {
    this.backgroundColorLocal = localStorage.getItem(`transcript_background_${this.messageId}`) || this.backgroundColor;

    // Detect if we're on mobile
    this.detectMobileDevice();

    // Add window resize listener
    window.addEventListener('resize', this.handleResize);

    // Calculate initial container size
    this.calculateContainerSize();

    // Start the cursor blinking animation
    this.startCursorBlink();
  },
  beforeDestroy() {
    // Clean up timers
    this.stopTypewriter();
    this.stopCursorBlink();
    this.clearPauseTimer();

    // Remove window resize listener
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    // Detect if we're on a mobile device
    detectMobileDevice() {
      this.isMobile = window.innerWidth <= 768;
    },

    // Handle window resize events
    handleResize() {
      const wasMobile = this.isMobile;
      this.detectMobileDevice();

      // Recalculate container size if mobile state changed
      if (wasMobile !== this.isMobile) {
        this.calculateContainerSize();
      }
    },

    // Calculate container dimensions based on text content
    calculateContainerSize() {
      if (!this.displayText) {
        this.containerWidth = 'auto';
        this.containerHeight = 'auto';
        return;
      }

      // Adjust dimensions for mobile
      const avatarSize = this.isMobile ? 60 : this.avatarWidth;
      const avatarGap = this.isMobile ? 12 : this.avatarSpacing;
      const charWidth = this.isMobile ? 8 : this.charWidthEstimate; // Smaller character width on mobile
      const lineHeight = this.isMobile ? 22 : this.lineHeightEstimate; // Smaller line height on mobile

      // Calculate max width based on screen size
      const maxWidthPercent = this.isMobile ? 0.92 : 0.75; // 92% of screen width on mobile, 75% on desktop
      const maxWidthPixels = this.maxWidthInPixels; // Absolute max width in pixels (800px by default)

      // Estimate text width based on character count but constrained to screen size and absolute max
      let contentWidth = Math.min(
        Math.max(this.displayText.length * charWidth, this.minContainerWidth),
        window.innerWidth * maxWidthPercent,
        maxWidthPixels // Apply absolute maximum width in pixels
      );

      // Calculate approximately how many lines we'll need
      const availableTextWidth = contentWidth - avatarSize - avatarGap - this.horizontalPadding;
      const charsPerLine = Math.max(10, Math.floor(availableTextWidth / charWidth)); // At least 10 chars per line

      // Calculate estimated lines with a minimum of 2 lines to prevent small containers
      const estimatedLines = Math.max(3, Math.ceil(this.displayText.length / charsPerLine));

      // Calculate height based on line count, ensuring enough space for avatar
      const textHeight = estimatedLines * lineHeight;

      // Ensure minimum content height matches avatar height for short text segments
      const minContentHeight = Math.max(this.minContainerHeight, avatarSize + 20); // avatar + margin
      const contentHeight = Math.max(textHeight + this.verticalPadding, minContentHeight);

      // Account for speaker name if shown
      const speakerNameHeight = this.showSpeaker ? (this.isMobile ? 20 : 28) : 0;

      // Set container dimensions
      this.containerWidth = `${contentWidth}px`;
      // Ensure height is sufficient for both content and avatar
      this.containerHeight = `${Math.min(contentHeight + speakerNameHeight, window.innerHeight * 0.6)}px`;
    },

    // Handle image loading errors
    handleImageError(event) {
      // Replace with fallback
      event.target.style.display = 'none';
      const container = event.target.parentNode;

      // Create fallback with initials
      const fallback = document.createElement('div');
      fallback.className = 'avatar-fallback';
      fallback.textContent = this.getSpeakerInitials();
      container.appendChild(fallback);
    },

    // Get speaker initials for fallback
    getSpeakerInitials() {
      if (!this.currentSpeaker) return '?';
      return this.currentSpeaker
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    },

    // Process text to display
    processText() {
      // Clean and truncate the text if necessary
      if (!this.fullText) {
        this.displayText = '';
        return;
      }

      // Remove HTML tags
      let cleanText = this.fullText.replace(/<[^>]*>/g, '');

      // Remove citation markers if any (like [1], [2])
      cleanText = cleanText.replace(/\[\d+\]/g, '');

      // Remove redundant spaces
      cleanText = cleanText.replace(/\s+/g, ' ').trim();

      // Truncate if necessary
      if (cleanText.length > this.maxLength) {
        cleanText = cleanText.substring(0, this.maxLength) + '...';
      }

      this.displayText = cleanText;
    },

    // Start the typewriter effect
    startTypewriter() {
      // Clear any existing timer
      this.stopTypewriter();
      this.clearPauseTimer();
      this.isPaused = false;

      // If already at the end, nothing to do
      if (this.typingIndex >= this.displayText.length) {
        this.cursorBlinking = true;
        return;
      }

      // Start the typing animation
      this.typeNextCharacter();
    },

    // Type next character with intelligent pauses
    typeNextCharacter() {
      // If we've reached the end of the text, stop typing
      if (this.typingIndex >= this.displayText.length) {
          this.cursorBlinking = true;
          this.isPaused = true;
          return;
      }

      // Type the next character
      this.typewriterText += this.displayText.charAt(this.typingIndex);

      // Check if we should pause after this character
      let shouldPause = false;
      let pauseMultiplier = 0;

      // Get current character and surrounding context
      const currentChar = this.displayText.charAt(this.typingIndex);
      const nextChar = this.typingIndex + 1 < this.displayText.length ? this.displayText.charAt(this.typingIndex + 1) : '';
      const prevChar = this.typingIndex > 0 ? this.displayText.charAt(this.typingIndex - 1) : '';

      // Look ahead for citation patterns (like c1c6)
      const lookAhead = this.typingIndex + 10 < this.displayText.length ?
          this.displayText.substring(this.typingIndex + 1, this.typingIndex + 10) :
          this.displayText.substring(this.typingIndex + 1);

      // Check if we're about to start a citation (about to type 'c' with digit after it)
      const isStartingCitation = currentChar === 'c' && /^\d/.test(nextChar);

      // Check if we've just finished a citation (just typed a digit after 'c' with no more digits ahead)
      const isEndingCitation = /\d/.test(currentChar) && prevChar && /c\d+c/.test(this.displayText.substring(this.typingIndex - 4, this.typingIndex + 1)) && !/\d/.test(nextChar);

      // Check for punctuation pauses
      if (this.pausePunctuation[currentChar]) {
          // Extract some context to check for special cases
          const contextAfter = this.displayText.substring(this.typingIndex + 1, this.typingIndex + 15);

          // Check if this is a period in an abbreviation (like B.Com)
          const isAbbreviation = /^[A-Z]\.([A-Z]|[a-z])/.test(prevChar + currentChar + nextChar);

          // Check if this is followed by a citation marker
          const followedByCitation = /^\s*c\d+/.test(contextAfter);

          // Only pause for periods that are sentence endings, not abbreviations
          if (currentChar === '.' && (isAbbreviation || nextChar === ' ' && /[A-Z]/.test(contextAfter.trim().charAt(0)))) {
          // This is likely a sentence ending period, so pause
          shouldPause = true;
          pauseMultiplier = this.pausePunctuation[currentChar];
          } else if (followedByCitation) {
          // Apply a pause before citation
          shouldPause = true;
          pauseMultiplier = 10; // Shorter pause before citation
          } else {
          // Standard punctuation pause
          shouldPause = true;
          pauseMultiplier = this.pausePunctuation[currentChar];
          }
      }

      // Handle citation starts and ends
      if (isStartingCitation) {
          shouldPause = true;
          pauseMultiplier = 10; // Pause before starting a citation
      } else if (isEndingCitation) {
          shouldPause = true;
          pauseMultiplier = 10; // Pause after ending a citation
      }

      this.typingIndex++;

      // Keep cursor steady during typing
      this.cursorBlinking = false;

      // Apply the appropriate pause if needed
      if (shouldPause && pauseMultiplier > 0) {
          this.addNaturalPause(pauseMultiplier);
      } else {
          // Otherwise continue with next character after the base delay
          this.scheduleNextCharacter();
      }
    },

    // Add a natural pause in the typing
    addNaturalPause(multiplier) {
      // Set paused state for animation
      this.isPaused = true;

      // Calculate pause duration based on punctuation type and speed
      const basePause = this.typingSpeed * multiplier;
      const adjustedPause = basePause / this.voiceSpeed;

      // Schedule the end of the pause
      this.pauseTimer = setTimeout(() => {
        this.isPaused = false;
        this.scheduleNextCharacter();
      }, adjustedPause);
    },

    // Schedule the next character with appropriate timing
    scheduleNextCharacter() {
      // Calculate delay based on speed
      const delay = this.getTypingDelay();

      // Schedule the next character
      this.typingTimer = setTimeout(() => {
        this.typeNextCharacter();
      }, delay);
    },

    // Get appropriate typing delay based on voice speed
    getTypingDelay() {
      // Adjust base typing speed for voice speed
      let delay = this.typingSpeed / this.voiceSpeed;

      // Add slight randomness for natural feel
      const randomFactor = 0.9 + (Math.random() * 0.2);
      delay *= randomFactor;

      return Math.max(10, Math.floor(delay));
    },

    // Stop the typewriter
    stopTypewriter() {
      if (this.typingTimer) {
        clearTimeout(this.typingTimer);
        this.typingTimer = null;
      }
    },

    // Clear any pause timer
    clearPauseTimer() {
      if (this.pauseTimer) {
        clearTimeout(this.pauseTimer);
        this.pauseTimer = null;
      }
    },

    // Reset the typewriter
    resetTypewriter() {
      this.stopTypewriter();
      this.clearPauseTimer();
      this.typingIndex = 0;
      this.typewriterText = '';
      this.isPaused = false;

      // Recalculate container size before restarting
      this.calculateContainerSize();

      this.startTypewriter();
    },

    // Start cursor blinking animation
    startCursorBlink() {
      // Toggle cursor blinking state every 500ms
      this.cursorTimer = setInterval(() => {
        // Only blink cursor when not actively typing
        if (this.typingIndex >= this.displayText.length) {
          this.cursorBlinking = !this.cursorBlinking;
        }
      }, 500);
    },

    // Stop cursor blinking animation
    stopCursorBlink() {
      if (this.cursorTimer) {
        clearInterval(this.cursorTimer);
        this.cursorTimer = null;
      }
    }
  }
}
</script>

<style scoped>
.live-transcript-overlay {
  position: absolute;
  bottom: 80px;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  pointer-events: none;
}

.live-transcript-container {
  position: relative;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center; /* Changed from flex-start to center for better vertical alignment */
  gap: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  backdrop-filter: blur(12px);
  overflow: hidden;
  min-height: 120px; /* Ensure minimum height for very short text */
}

/* Ultra modern styling */
.ultra-modern {
  border: 1px solid rgba(255, 255, 255, 0.1);
  /* Background color comes from inline style based on props/localstorage */
  animation: modernAppear 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes modernAppear {
  0% { transform: translateY(20px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}

/* Animated background elements */
.animated-bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: -1;
}

.bg-element {
  position: absolute;
  background: linear-gradient(135deg, rgba(80, 100, 255, 0.1), rgba(120, 200, 255, 0.05));
  border-radius: 50%;
  animation: moveElement 20s infinite alternate;
  opacity: 0.4;
}

.bg-element:nth-child(1) {
  width: 200px;
  height: 200px;
  top: -100px;
  right: -50px;
  animation-delay: 0s;
}

.bg-element:nth-child(2) {
  width: 150px;
  height: 150px;
  bottom: -50px;
  left: 20%;
  animation-delay: -5s;
}

.bg-element:nth-child(3) {
  width: 100px;
  height: 100px;
  top: 30%;
  left: -30px;
  animation-delay: -10s;
}

@keyframes moveElement {
  0% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(20px, 10px) scale(1.1); }
  100% { transform: translate(-10px, 15px) scale(0.95); }
}

/* Speaker section */
.speaker-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.speaker-name-container {
  margin-bottom: 6px;
  width: 100%;
}

.speaker-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 14px;
  font-weight: 500;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.speaker-status {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #888;
  position: relative;
}

.speaker-status.active {
  background-color: #4CAF50;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.6);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
  70% { box-shadow: 0 0 0 6px rgba(76, 175, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

/* Avatar styling */
.transcript-speaker-avatar {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.transcript-speaker-avatar.speaking {
  transform: scale(1.05);
  border-color: rgba(255, 255, 255, 0.5);
}

.avatar-glow {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.avatar-glow.active {
  opacity: 1;
  box-shadow: 0 0 20px 5px rgba(100, 200, 255, 0.4);
  animation: glowPulse 2s infinite;
}

@keyframes glowPulse {
  0% { box-shadow: 0 0 15px 5px rgba(100, 200, 255, 0.4); }
  50% { box-shadow: 0 0 25px 8px rgba(100, 200, 255, 0.6); }
  100% { box-shadow: 0 0 15px 5px rgba(100, 200, 255, 0.4); }
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #3a4a6b, #2c3e50);
  color: white;
  font-weight: bold;
  font-size: 28px;
  letter-spacing: 1px;
}

/* Enhanced wave animation */
.avatar-wave-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 24px;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 3px;
  padding: 4px;
  transition: background-color 0.3s ease;
}

.avatar-wave-indicator.active {
  background: rgba(25, 118, 210, 0.7);
  background: linear-gradient(to right, rgba(25, 118, 210, 0.7), rgba(66, 165, 245, 0.7));
}

.wave-bar {
  width: 3px;
  height: 3px;
  border-radius: 1.5px;
  background: white;
  transition: height 0.2s ease;
}

.active .wave-bar {
  animation: enhancedWaveAnimation 0.8s ease-in-out infinite;
}

.active .wave-bar:nth-child(1) { animation-delay: -0.6s; }
.active .wave-bar:nth-child(2) { animation-delay: -0.4s; }
.active .wave-bar:nth-child(3) { animation-delay: -0.2s; }
.active .wave-bar:nth-child(4) { animation-delay: 0s; }
.active .wave-bar:nth-child(5) { animation-delay: -0.8s; }

@keyframes enhancedWaveAnimation {
  0% { height: 3px; }
  40% { height: 16px; }
  80% { height: 5px; }
  100% { height: 3px; }
}

/* Content styling */
.transcript-content {
  flex: 1;
  overflow: hidden;
}

.transcript-text-container {
  position: relative;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 90px; /* Ensure minimum height to prevent avatar cutoff */
  display: flex;
  align-items: center;
}

.transcript-text {
  color: white;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  min-height: 1.5em;
  word-wrap: break-word;
  letter-spacing: 0.3px;
  width: 100%;
}

.cursor {
  display: inline-block;
  color: white;
  font-weight: bold;
  margin-left: 1px;
  position: relative;
  top: -1px;
}

.cursor.blink {
  animation: blink 1s step-end infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .live-transcript-overlay {
    bottom: 16px;
  }

  .live-transcript-container {
    max-width: 92%;
    padding: 12px;
    border-radius: 12px;
  }

  .transcript-speaker-avatar {
    width: 60px;
    height: 60px;
    min-width: 60px;
  }

  .avatar-fallback {
    font-size: 18px;
  }

  .avatar-wave-indicator {
    height: 20px;
    padding: 3px;
  }

  .wave-bar {
    width: 2px;
  }

  @keyframes enhancedWaveAnimation {
    0% { height: 2px; }
    40% { height: 12px; }
    80% { height: 4px; }
    100% { height: 2px; }
  }
  .transcript-text {
    font-size: 16px;
  }

}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05), rgba(0, 0, 0, 0.05));
  pointer-events: none;
  z-index: 0;
}

</style>
